import slugify from "slugify";

//#region src/lib/base.ts
let extensionsApplied = false;
function ensureExtensions() {
	if (extensionsApplied) return;
	slugify.extend({
		"★": "star",
		"†": "cross",
		"?": "question",
		"!": "exclamation"
	});
	extensionsApplied = true;
}
ensureExtensions();
/**
* Universal slugify function
* Used by all games for basic string → slug conversion
*
* @param text - The text to slugify
* @param options - Slugify options (strict mode, locale)
* @returns URL-safe slug string
*
* @example
* baseSlugify("Test Expansion") // "test-expansion"
* baseSlugify("Test & Group") // "test-and-group"
*/
function baseSlugify(text, options = {}) {
	const { strict = true, locale = "en" } = options;
	return slugify(text, {
		lower: true,
		strict,
		trim: true,
		locale
	});
}
/**
* Fallback slugify for strings that produce empty slugs
* Tries strict mode first, falls back to non-strict if empty
*
* This handles edge cases like:
* - Strings with only special characters
* - Unicode strings that don't map to ASCII in strict mode
*
* @param text - The text to slugify
* @returns URL-safe slug string (guaranteed non-empty if input is non-empty)
*
* @example
* fallbackSlugify("♠♥♦♣") // Falls back to non-strict mode
* fallbackSlugify("日本語") // Falls back to non-strict with transliteration
*/
function fallbackSlugify(text) {
	let slug = baseSlugify(text, { strict: true });
	if (slug === "") slug = baseSlugify(text, { strict: false });
	return slug;
}
/**
* Sealed product slug: expansion-name format (no finish/printNumber/variant)
* Used for all games - sealed products have consistent slugging across games
*
* @param expansion - Expansion slug
* @param name - Product name
* @returns Sealed product slug in format: expansion-name
*
* @example
* sealedProductSlug("alpha", "Booster Box") // "alpha-booster-box"
* sealedProductSlug("beta", "Precon Deck") // "beta-precon-deck"
*/
function sealedProductSlug(expansion, name) {
	const nameSlug = baseSlugify(name);
	return expansion ? `${expansion}-${nameSlug}` : nameSlug;
}
/**
* Generate a finish-agnostic product slug.
* Single source of truth for both importers and admin.
*
* Pattern: {expansion}-{name}-{printNumber?}-{variant?}-{rarity?}
*
* Note: finish is NOT included in the slug. Products with multiple finishes
* (Standard, Foil, etc.) share the same slug.
*
* @param printing - Object containing product attributes
* @returns URL-safe product slug
*
* @example
* generateProductSlug({ expansion: "clb", name: "Abdel Adrian", printNumber: "2" })
* // "clb-abdel-adrian-2"
*
* generateProductSlug({ expansion: "wtr", name: "Bravo", printNumber: "001", variant: "extended-art" })
* // "wtr-bravo-001-extended-art"
*/
function generateProductSlug(printing) {
	const toPart = (value) => {
		if (!value) return void 0;
		const slug = baseSlugify(value) || fallbackSlugify(value);
		return slug === "" ? void 0 : slug;
	};
	const parts = [toPart(printing.expansion) ?? "", toPart(printing.name) ?? ""];
	if (printing.printNumber) {
		const part = toPart(printing.printNumber);
		if (part) parts.push(part);
	}
	if (printing.variant) {
		const part = toPart(printing.variant);
		if (part) parts.push(part);
	}
	if (printing.rarity) {
		const part = toPart(printing.rarity);
		if (part) parts.push(part);
	}
	return parts.filter((p) => p !== "").join("-");
}
/**
* Generate nameSlug with game-specific logic.
* Single source of truth for both importers and admin.
*
* @param name - The product/card name
* @param gameSlug - Optional game slug to apply game-specific logic
* @returns URL-safe name slug
*
* @example
* generateNameSlug("Bravo (Red)", "fab") // "bravo" (color stripped)
* generateNameSlug("Talisman ★", "mtg") // "talisman-star"
* generateNameSlug("Regular Card", "pokemon") // "regular-card"
*/
function generateNameSlug(name, gameSlug) {
	switch (gameSlug) {
		case "fab": {
			const baseName = name.replace(/\s*\([^)]+\)$/, "");
			const slug = baseSlugify(baseName);
			return slug === "" ? baseSlugify(baseName, { strict: false }) : slug;
		}
		case "mtg": {
			let normalized = name;
			for (const [char, replacement] of Object.entries({
				"★": "star",
				"//": "-"
			})) normalized = normalized.replaceAll(char, replacement);
			const slug = baseSlugify(normalized);
			return slug === "" ? baseSlugify(normalized, { strict: false }) : slug;
		}
		case "gundam": {
			const slug = baseSlugify(name.replace(/\s*\((?:C|U|R|LR)\+{1,2}\)\s*$/, ""));
			return slug === "" ? fallbackSlugify(name) : slug;
		}
		default: {
			const slug = baseSlugify(name);
			return slug === "" ? fallbackSlugify(name) : slug;
		}
	}
}

//#endregion
export { sealedProductSlug as a, generateProductSlug as i, fallbackSlugify as n, generateNameSlug as r, baseSlugify as t };
//# sourceMappingURL=base-DTKOF4VR.js.map